/**
 * Laying serialised HTML back out as source somebody can read.
 *
 * The document model emits one unbroken line. That is fine for a browser and
 * useless for the other half of this editor: the body stays editable as HTML
 * source in the second tab, and a twelve-thousand-character line is not source.
 * It also makes every save a whole-file diff, which hides what actually changed
 * between a seeded guide and its edited copy.
 *
 * The layout follows the seeds, because that is what the twenty-two written
 * guides look like and a rewrite of all of them is not a formatting choice.
 */

/** Holds other blocks: opens a line, indents what follows, closes a line. */
const CONTAINERS = new Set(["ul", "ol", "table", "thead", "tbody", "div", "blockquote"]);

/**
 * Gets a line of its own and keeps its contents on it.
 *
 * `tr` is here rather than in the containers because the handbook writes a row
 * as one line (`<tr><td>a</td><td>b</td></tr>`) and a cell per line turns a
 * four-column table into a forty-line block nobody can read.
 */
const LEAVES = new Set(["p", "h2", "h3", "h4", "li", "tr", "figure", "pre"]);

/** No closing partner, so they are a line by themselves wherever they start. */
const VOID_BLOCKS = new Set(["hr"]);

const TOKEN_RE = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b((?:"[^"]*"|[^>])*)>|[^<]+/g;

export function formatKbHtml(html: string): string {
  const lines: string[] = [];
  let depth = 0;
  /** The leaf currently being collected, and the text of its line. */
  let leaf: string | null = null;
  let buffer = "";

  const pad = () => "  ".repeat(depth);
  const gap = () => {
    // A blank line between top-level blocks, and only there: inside a list or a
    // table the blocks belong together and spacing them out reads as a break.
    if (depth === 0 && lines.length && lines[lines.length - 1] !== "") lines.push("");
  };

  for (const m of html.matchAll(TOKEN_RE)) {
    const whole = m[0];
    const tag = (m[1] ?? "").toLowerCase();
    const closing = whole.startsWith("</");

    // Inside a leaf everything is part of its line until the leaf closes.
    if (leaf) {
      buffer += whole;
      if (closing && tag === leaf) {
        lines.push(pad() + buffer);
        leaf = null;
        buffer = "";
      }
      continue;
    }

    if (!tag) {
      // Text between blocks. The seeds have none, and a serialiser that emits
      // some would be telling us about content with nowhere to live.
      if (whole.trim()) lines.push(pad() + whole.trim());
      continue;
    }

    if (CONTAINERS.has(tag)) {
      if (closing) {
        depth = Math.max(0, depth - 1);
        lines.push(pad() + whole);
      } else {
        gap();
        lines.push(pad() + whole);
        depth += 1;
      }
      continue;
    }

    if (VOID_BLOCKS.has(tag)) {
      gap();
      lines.push(pad() + whole);
      continue;
    }

    if (LEAVES.has(tag) && !closing) {
      gap();
      // An empty element closed in the same token (`<figure ... />` never
      // happens here, but `<figure></figure>` arrives as two tokens).
      leaf = tag;
      buffer = whole;
      continue;
    }

    // A stray closing tag for something we never opened as a leaf, or an inline
    // tag at block level. Keep it rather than drop it: losing markup quietly is
    // the one thing this whole file exists to prevent.
    lines.push(pad() + whole);
  }

  if (buffer.trim()) lines.push(pad() + buffer);
  return lines.join("\n");
}
